import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRunning, FaRoad, FaMountain, FaFlagCheckered } from "react-icons/fa";
import onboardingDistance from "./img/onboarding/onboardingDistance.png";
import onboardingRacedate from "./img/onboarding/onboardingRacedate.jpg";
import onboardingTime from "./img/onboarding/onboardingTime.jpg";
import "./run.css";
import { repo } from "./ch/planrepo";
import { TrainingPlan } from "./@types/app";
import { validateOnboarding } from "./ch/onboarding2Validation";
import { encodeLongRunDay, encodeTrainingDays } from "./ch/shareableConfig";
import type { dayOfWeek } from "types/app";
import { TopNav } from "./components/TopNav";

// Show 3 images for the visual side on larger screens
const ONBOARDING_IMAGES = [onboardingDistance, onboardingTime, onboardingRacedate];

type RunData = {
  distance: string;
  raceDate: Date | null;
  trainingDays: string[];
  longRunDay: string;
  goalTime: string;
};

const DISTANCE_OPTIONS = [
  { id: "5k", label: "5K", desc: "3.1 miles", icon: <FaRunning /> },
  { id: "10k", label: "10K", desc: "6.2 miles", icon: <FaRoad /> },
  {
    id: "half",
    label: "Half Marathon",
    desc: "13.1 miles / 21.1 km",
    icon: <FaMountain />,
  },
  {
    id: "full",
    label: "Marathon",
    desc: "26.2 miles / 42.2 km",
    icon: <FaFlagCheckered />,
  },
];

const DAYS_OF_WEEK = [
  { full: "Monday", abbr: "Mon" },
  { full: "Tuesday", abbr: "Tue" },
  { full: "Wednesday", abbr: "Wed" },
  { full: "Thursday", abbr: "Thu" },
  { full: "Friday", abbr: "Fri" },
  { full: "Saturday", abbr: "Sat" },
  { full: "Sunday", abbr: "Sun" },
];

const GOAL_TIME_GUIDE = {
  "5k": [
    { level: "Beginner", time: "30–35 min", value: "beginner" },
    { level: "Intermediate", time: "24–29 min", value: "intermediate" },
    { level: "Advanced", time: "20–23 min", value: "advanced" },
    { level: "Elite", time: "< 20 min", value: "elite" },
  ],
  "10k": [
    { level: "Beginner", time: "60–70 min", value: "beginner" },
    { level: "Intermediate", time: "48–59 min", value: "intermediate" },
    { level: "Advanced", time: "42–47 min", value: "advanced" },
    { level: "Elite", time: "< 42 min", value: "elite" },
  ],
  "half": [
    { level: "Beginner", time: "2h15m–2h30m", value: "beginner" },
    { level: "Intermediate", time: "1h45m–2h10m", value: "intermediate" },
    { level: "Advanced", time: "1h35m–1h44m", value: "advanced" },
    { level: "Elite", time: "< 1h35m", value: "elite" },
  ],
  "full": [
    { level: "Beginner", time: "4h45m–5h30m", value: "beginner" },
    { level: "Intermediate", time: "3h45m–4h30m", value: "intermediate" },
    { level: "Advanced", time: "3h15m–3h29m", value: "advanced" },
    { level: "Elite", time: "< 3h15m", value: "elite" },
  ],
} as const;

const Run = () => {
  const navigate = useNavigate();

  // Use the same default date the calendar visually highlights.
  // This avoids raceDate staying null when the user doesn't click a date.
  const defaultRaceDate = new Date(
    new Date().setMonth(new Date().getMonth() + 2),
  );

  const [formData, setFormData] = useState<RunData>({
    distance: "",
    raceDate: defaultRaceDate,
    trainingDays: [],
    longRunDay: "",
    goalTime: "",
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<TrainingPlan | null>(null);
  const [recommendedDays, setRecommendedDays] = useState<number>(0);

  // Change image based on scroll position
  useEffect(() => {
    const computeIndex = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrollableHeight = Math.max(1, documentHeight - windowHeight);
      const raw = scrollPosition / scrollableHeight;
      const clamped = Math.max(0, Math.min(1, raw));

      const idx = Math.min(
        Math.round(clamped * (ONBOARDING_IMAGES.length - 1)),
        ONBOARDING_IMAGES.length - 1,
      );
      setCurrentImageIndex(idx);
    };

    computeIndex();

    window.addEventListener("scroll", computeIndex, { passive: true });
    window.addEventListener("resize", computeIndex);
    return () => {
      window.removeEventListener("scroll", computeIndex);
      window.removeEventListener("resize", computeIndex);
    };
  }, []);

  // Load the selected plan when distance and goalTime change
  useEffect(() => {
    const loadPlan = async () => {
      if (!formData.distance || !formData.goalTime) {
        setSelectedPlan(null);
        setRecommendedDays(0);
        return;
      }

      const distanceMap: { [key: string]: string } = {
        "5k": "5k",
        "10k": "10k",
        "half": "half",
        "full": "marathon",
      };

      const planPrefix = distanceMap[formData.distance];
      const planId = `${planPrefix}-${formData.goalTime}`;

      try {
        const planSummary = repo.find(planId);
        const plan = await repo.fetch(planSummary);
        setSelectedPlan(plan);

        // Required days should reflect the busiest week (max non-rest workouts across the plan)
        if (plan.schedule && plan.schedule.length > 0) {
          const countWeek = (week: any): number => {
            const workouts = week?.workouts;
            if (!Array.isArray(workouts)) return 0;
            return workouts.filter((workout: any) => {
              const title = String(workout?.title ?? "").toLowerCase();
              return title && title !== "rest" && !title.includes("rest");
            }).length;
          };

          const busiest = plan.schedule.reduce((max: number, week: any) => {
            return Math.max(max, countWeek(week));
          }, 0);

          setRecommendedDays(busiest);
        } else {
          setRecommendedDays(0);
        }
      } catch (error) {
        console.error("Error loading plan:", error);
        setSelectedPlan(null);
        setRecommendedDays(0);
      }
    };

    loadPlan();
  }, [formData.distance, formData.goalTime]);

  const toggleTrainingDay = (day: string) => {
    setFormData((prev) => {
      const isSelected = prev.trainingDays.includes(day);
      const newTrainingDays = isSelected
        ? prev.trainingDays.filter((d) => d !== day)
        : [...prev.trainingDays, day];

      const newLongRunDay = newTrainingDays.includes(prev.longRunDay)
        ? prev.longRunDay
        : "";

      return { ...prev, trainingDays: newTrainingDays, longRunDay: newLongRunDay };
    });
  };

  const setLongRunDay = (day: string) => {
    setFormData((prev) => {
      const trainingDays = prev.trainingDays.includes(day)
        ? prev.trainingDays
        : [...prev.trainingDays, day];
      return { ...prev, trainingDays, longRunDay: day };
    });
  };

  const getGoalTimeOptions = () => {
    if (!formData.distance) return [];
    return GOAL_TIME_GUIDE[formData.distance as keyof typeof GOAL_TIME_GUIDE] || [];
  };

  const isFormValid = () => {
    const result = validateOnboarding({
      distance: formData.distance,
      goalTime: formData.goalTime,
      raceDate: formData.raceDate,
      trainingDays: formData.trainingDays,
      longRunDay: formData.longRunDay,
      selectedPlanLoaded: selectedPlan !== null,
      recommendedDays,
    });
    return result.valid;
  };

  const handleSubmit = () => {
    if (!selectedPlan || !formData.raceDate) {
      return;
    }

    if (formData.trainingDays.length < recommendedDays) {
      alert(
        `Please select at least ${recommendedDays} training days to match the busiest week in your plan.`,
      );
      return;
    }

    const resolvedLongRunDay =
      formData.longRunDay ||
      (formData.trainingDays.includes("Sunday")
        ? "Sunday"
        : formData.trainingDays.includes("Saturday")
          ? "Saturday"
          : formData.trainingDays[0] || "");

    const config = {
      planId: selectedPlan.id,
      raceDate: formData.raceDate.toISOString(),
      trainingDays: formData.trainingDays,
      longRunDay: resolvedLongRunDay,
    };

    localStorage.setItem("onboardingConfig", JSON.stringify(config));

    const params = new URLSearchParams({
      u: "km",
      p: selectedPlan.id,
      d: formData.raceDate.toISOString(),
      s: "1",
    });

    const td = encodeTrainingDays(formData.trainingDays as dayOfWeek[]);
    const lr = encodeLongRunDay(resolvedLongRunDay as dayOfWeek);
    if (td) params.set("td", td);
    if (lr) params.set("lr", lr);

    navigate(`/dashboard?${params.toString()}`);
  };

  return (
    <div className="rungenerate-container">
      <TopNav active="running" />

      <div className="rungenerate-content">
        {/* Hero Section with Scroll-based Images */}
        <div className="rungenerate-hero">
          <div className="hero-content">
            <div className="hero-image-wrapper">
              <img
                src={ONBOARDING_IMAGES[currentImageIndex]}
                alt="Running"
                className="hero-image"
              />
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="rungenerate-form">
          <div className="form-container">
            {/* Step 1: Distance */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">1</span>
                <div>
                  <h2 className="section-title">Choose Your Distance</h2>
                  <p className="section-subtitle">What race are you training for?</p>
                </div>
              </div>

              <div className="options-grid">
                {DISTANCE_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className={`option-card ${formData.distance === option.id ? "selected" : ""}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        distance: option.id,
                        goalTime: "",
                        trainingDays: [],
                        longRunDay: "",
                      }))
                    }
                  >
                    <div className="option-icon">{option.icon}</div>
                    <h3 className="option-title">{option.label}</h3>
                    <p className="option-desc">{option.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 2: Goal Time */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">2</span>
                <div>
                  <h2 className="section-title">Choose Your Goal Time</h2>
                  <p className="section-subtitle">Pick the closest option</p>
                </div>
              </div>

              <div className="goal-time-grid">
                {getGoalTimeOptions().map((opt) => (
                  <div
                    key={opt.value}
                    className={`goal-time-card ${formData.goalTime === opt.value ? "selected" : ""}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        goalTime: opt.value,
                        trainingDays: [],
                        longRunDay: "",
                      }))
                    }
                  >
                    <h3 className="goal-level">{opt.level}</h3>
                    <p className="goal-time">{opt.time}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 3: Race Date */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">3</span>
                <div>
                  <h2 className="section-title">Choose Your Race Date</h2>
                  <p className="section-subtitle">When is your event?</p>
                </div>
              </div>

              <div className="date-picker-container">
                <DatePicker
                  selected={formData.raceDate}
                  onChange={(date: Date | null) =>
                    setFormData((prev) => ({
                      ...prev,
                      raceDate: date,
                    }))
                  }
                  minDate={new Date()}
                  openToDate={defaultRaceDate}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Click to select a date"
                  className="date-input"
                  inline
                />
              </div>
            </div>

            {/* Step 4: Training Schedule */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">4</span>
                <div>
                  <h2 className="section-title">Set Your Training Schedule</h2>
                  <p className="section-subtitle">
                    Choose your training days and long run day
                  </p>
                  {recommendedDays > 0 && (
                    <p className="recommended-days">
                      Your plan’s has up to {recommendedDays} workout days
                      <br />
                      Selected {formData.trainingDays.length} / Required {recommendedDays}
                    </p>
                  )}
                </div>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">What days of the week do you train?</h3>
                {recommendedDays > 0 && (
                  <p className="section-subtitle" style={{ marginTop: 6 }}>
                    Select at least {recommendedDays} days to continue.
                  </p>
                )}
                <div className="days-grid">
                  {DAYS_OF_WEEK.map((day) => (
                    <div
                      key={day.full}
                      className={`day-card ${formData.trainingDays.includes(day.full) ? "selected" : ""}`}
                      onClick={() => toggleTrainingDay(day.full)}
                    >
                      {day.abbr}
                    </div>
                  ))}
                </div>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">What day is your long run?</h3>
                <p className="section-subtitle" style={{ marginTop: 6 }}>
                  Optional — if you skip this, we’ll pick a default.
                </p>
                <div className="days-grid">
                  {DAYS_OF_WEEK.map((day) => {
                    const isAvailable = formData.trainingDays.includes(day.full);
                    return (
                      <div
                        key={day.full}
                        className={`day-card ${formData.longRunDay === day.full ? "selected" : ""} ${!isAvailable ? "disabled" : ""} ${formData.longRunDay === day.full ? "long-run-selected" : ""}`}
                        onClick={() => isAvailable && setLongRunDay(day.full)}
                      >
                        {day.abbr}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="form-footer">
              <button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={!isFormValid()}
              >
                Create My Training Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Run;
